import { auth } from "../firebase";

// Refresh a few minutes early so a long-lived tab does not send a token that
// is already expired (or about to be). Firebase usually refreshes on its own,
// but a stuck IndexedDB / clock-skew session is exactly the "close the window
// or log in again and it works" failure clients report.
const REFRESH_WITHIN_MS = 5 * 60 * 1000;

/**
 * Bearer + JSON headers for Cloud Function calls.
 *
 * @param {object} [options]
 * @param {boolean} [options.forceRefresh] skip the expiry check and always
 *   mint a fresh ID token (used by the axios 401 retry).
 * @param {string} [options.signedOutMessage] thrown when there is no user
 * @returns {Promise<{Authorization: string, "Content-Type": string}>}
 */
export async function getAuthHeaders({
  forceRefresh = false,
  signedOutMessage = "You must be signed in to continue",
} = {}) {
  const user = auth.currentUser;

  if (!user) {
    throw new Error(signedOutMessage);
  }

  let shouldForce = forceRefresh;

  if (!shouldForce) {
    try {
      const tokenResult = await user.getIdTokenResult();
      const expiresAt = new Date(tokenResult.expirationTime).getTime();
      if (!Number.isFinite(expiresAt) || expiresAt - Date.now() < REFRESH_WITHIN_MS) {
        shouldForce = true;
      }
    } catch (error) {
      shouldForce = true;
    }
  }

  const token = await user.getIdToken(shouldForce);

  if (!token) {
    throw new Error(signedOutMessage);
  }

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}
